/// <reference path="../../nativewind-augmentation.d.ts" />
// Copyright (c) Ping. All rights reserved.
//
// Account-recovery backup pane.
//
// What this does:
//   - Generates a strong recovery key (120-bit grouped base32) on
//     demand so the user never has to invent a passphrase. The same
//     generator backs the post-signup recovery prompt in
//     `auth/steps/RecoveryBackupSetupScreen`.
//   - Lets the user copy or download the key, then encrypts the SDK
//     identity export with it (PBKDF2-SHA256 600k + AES-256-GCM via
//     `recoveryAead`) and uploads the ciphertext to /v1/recovery/blobs.
//   - Shows the current backup status when the host wires the
//     read-only `status` prop (uploadedAt / skippedAt / never).
//
// What this does NOT do (and why):
//   - Download / decrypt. That path needs a recovery JWT minted by
//     `POST /v1/auth/recover/verify` after an OTP challenge — the
//     auth-flow screen for that lives outside Settings.
//
// Threat model: the BE only sees ciphertext + the wall-clock upload
// time. The passphrase never leaves the device. If the user loses the
// passphrase the blob is irrecoverable — that's why the pane funnels
// users through copy / download before letting them upload.

import {
  Check,
  CircleAlert,
  Copy,
  Download,
  RefreshCw,
  ShieldCheck,
} from 'lucide-react-native';
import { useCallback, useMemo, useState } from 'react';
import { Pressable, Text, View } from 'react-native';

import { generateRecoveryPassphrase } from '../../crypto/recoveryPassphrase';
import { RecoveryMnemonicSetupScreen } from '../../auth/steps/RecoveryMnemonicSetupScreen';
import { SettingsRow } from '../SettingsRow';

export interface RecoveryUploadInput {
  readonly passphrase: string;
}

export interface RecoveryUploadResult {
  readonly bytes: number;
}

/**
 * Mirrors the local marker shape written by the post-signup host so
 * Settings can render the same "Last backup: …" copy without a
 * separate persistence layer.
 */
export type RecoveryBackupStatus =
  | { readonly kind: 'completed'; readonly uploadedAt: number; readonly bytes: number }
  | { readonly kind: 'skipped'; readonly skippedAt: number }
  | { readonly kind: 'never' };

export interface RecoveryPaneProps {
  /**
   * Encrypt + upload the local identity. Host implements as:
   *   const identityExport = await identityManager.unwrapIdentity();
   *   const ciphertext = await encryptRecoveryBlob(identityExport, passphrase);
   *   await postRecoveryBlob({ ciphertext });
   *   return { bytes: identityExport.length };
   * Rejection messages bubble up verbatim.
   */
  readonly onUpload: (input: RecoveryUploadInput) => Promise<RecoveryUploadResult>;
  /**
   * Host-supplied clipboard write. Same shape as the auth screen.
   * Wired to `navigator.clipboard.writeText` on web; macOS host uses
   * the native pasteboard.
   */
  readonly onCopyToClipboard: (text: string) => Promise<void> | void;
  /**
   * Host-supplied file download. Receives the plaintext key text and
   * a recommended filename.
   */
  readonly onDownload: (text: string, filename: string) => Promise<void> | void;
  /**
   * Current local backup status. When omitted, the status row is
   * hidden — useful for hosts that haven't wired the marker store
   * yet (or for tests).
   */
  readonly status?: RecoveryBackupStatus;
  /**
   * Reveal the recovery phrase stored locally after the background
   * post-signup backup. Resolves to the 12-word mnemonic, or `null` if
   * nothing is stored (already saved + purged, or never backed up).
   * When provided, the pane shows a "View recovery phrase" affordance.
   */
  readonly onRevealMnemonic?: () => Promise<string | null>;
  /**
   * Called when the user confirms they've written the revealed phrase
   * down. Host should set the "saved confirmed" flag and PURGE the
   * stored mnemonic.
   */
  readonly onConfirmMnemonicSaved?: () => Promise<void> | void;
  /**
   * Rotate the recovery key (V3): generate a fresh mnemonic, build +
   * upload a new V3 blob, and return the NEW mnemonic for display so the
   * user can write it down. When provided, the legacy passphrase
   * "Encrypt & upload backup" form is replaced by a single "Rotate
   * recovery key" action.
   */
  readonly onRotateRecoveryKey?: () => Promise<string>;
}

export function RecoveryPane(props: RecoveryPaneProps): JSX.Element {
  const {
    onUpload,
    onCopyToClipboard,
    onDownload,
    status,
    onRevealMnemonic,
    onConfirmMnemonicSaved,
    onRotateRecoveryKey,
  } = props;

  // When the host wires the V3 mnemonic surface, render the modern
  // reveal/rotate pane (the background backup already secured the
  // account; this lets the user view + save the phrase). Otherwise fall
  // back to the legacy passphrase upload form below.
  if (onRevealMnemonic !== undefined && onRotateRecoveryKey !== undefined) {
    return (
      <RecoveryMnemonicPane
        {...(status !== undefined ? { status } : {})}
        onRevealMnemonic={onRevealMnemonic}
        {...(onConfirmMnemonicSaved !== undefined
          ? { onConfirmMnemonicSaved }
          : {})}
        onRotateRecoveryKey={onRotateRecoveryKey}
        onCopyToClipboard={onCopyToClipboard}
      />
    );
  }

  return (
    <LegacyRecoveryPane
      onUpload={onUpload}
      onCopyToClipboard={onCopyToClipboard}
      onDownload={onDownload}
      {...(status !== undefined ? { status } : {})}
    />
  );
}

// ── V3 mnemonic pane: reveal stored phrase + rotate key ─────────────

interface RecoveryMnemonicPaneProps {
  readonly status?: RecoveryBackupStatus;
  readonly onRevealMnemonic: () => Promise<string | null>;
  readonly onConfirmMnemonicSaved?: () => Promise<void> | void;
  readonly onRotateRecoveryKey: () => Promise<string>;
  readonly onCopyToClipboard: (text: string) => Promise<void> | void;
}

function RecoveryMnemonicPane({
  status,
  onRevealMnemonic,
  onConfirmMnemonicSaved,
  onRotateRecoveryKey,
  onCopyToClipboard,
}: RecoveryMnemonicPaneProps): JSX.Element {
  // `idle` → list view; `reveal` → show the stored phrase to save;
  // `rotate` → show the freshly-rotated phrase to save.
  const [view, setView] = useState<'idle' | 'reveal' | 'rotate'>('idle');
  const [mnemonic, setMnemonic] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmedMsg, setConfirmedMsg] = useState<string | null>(null);

  const statusLine = useMemo(() => describeStatus(status, null), [status]);

  const handleReveal = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const phrase = await onRevealMnemonic();
      if (phrase === null) {
        setError(
          "Your recovery phrase isn't available to view on this device. Rotate your recovery key to generate a new one.",
        );
        return;
      }
      setMnemonic(phrase);
      setView('reveal');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }, [onRevealMnemonic]);

  const handleRotate = useCallback(async () => {
    setBusy(true);
    setError(null);
    setConfirmedMsg(null);
    try {
      const phrase = await onRotateRecoveryKey();
      setMnemonic(phrase);
      setView('rotate');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }, [onRotateRecoveryKey]);

  const handleConfirmSaved = useCallback(async (): Promise<void> => {
    // The mnemonic screen owns the "I've written these down" gate; we
    // only persist the confirmation + purge the stored phrase here.
    if (onConfirmMnemonicSaved !== undefined) {
      await onConfirmMnemonicSaved();
    }
    setMnemonic(null);
    setView('idle');
    setConfirmedMsg('Recovery phrase saved.');
  }, [onConfirmMnemonicSaved]);

  if ((view === 'reveal' || view === 'rotate') && mnemonic !== null) {
    return (
      <RecoveryMnemonicSetupScreen
        mnemonic={mnemonic}
        onComplete={handleConfirmSaved}
        onSkip={() => {
          setMnemonic(null);
          setView('idle');
        }}
        onCopyToClipboard={onCopyToClipboard}
      />
    );
  }

  return (
    <View className="flex flex-col gap-4">
      <SettingsRow
        variant="hero"
        testID="settings-recovery-intro"
        title="Account recovery"
        description="Your account is backed up with an encrypted recovery phrase. Write down the 12 words and keep them somewhere safe — they're the only way to restore your account if you lose every device. Ping cannot reset them for you."
      />

      {statusLine !== null ? (
        <SettingsRow testID="settings-recovery-backup-status" title="Backup status">
          <StatusBadge tone={statusLine.tone} text={statusLine.text} />
        </SettingsRow>
      ) : null}

      <SettingsRow
        testID="settings-recovery-phrase"
        title="Recovery phrase"
        description="View your 12-word recovery phrase so you can write it down somewhere safe."
      >
        <View className="flex flex-col gap-3 max-w-[420px]">
          <Pressable
            onPress={() => {
              void handleReveal();
            }}
            disabled={busy}
            className={`self-start flex flex-row items-center gap-2 px-4 py-2 rounded-pill ${
              busy
                ? 'bg-light-surface-elevated dark:bg-dark-surface-elevated opacity-60'
                : 'bg-light-accent-brand dark:bg-dark-accent-brand active:opacity-80'
            }`}
          >
            <ShieldCheck size={16} strokeWidth={1.75} color={busy ? '#B5BAC3' : '#FFFFFF'} />
            <Text
              className={`text-[13px] leading-[18px] font-semibold ${
                busy
                  ? 'text-light-text-secondary dark:text-dark-text-secondary'
                  : 'text-light-text-onAccent dark:text-dark-text-onAccent'
              }`}
            >
              {busy ? 'Working…' : 'Reveal recovery phrase'}
            </Text>
          </Pressable>

          {confirmedMsg !== null ? (
            <View className="flex flex-row items-center gap-2">
              <Check size={14} strokeWidth={2} color="#3CCB4F" />
              <Text className="text-[12px] leading-[16px] text-[#3CCB4F]">
                {confirmedMsg}
              </Text>
            </View>
          ) : null}

          {error !== null ? (
            <View className="flex flex-row items-center gap-2">
              <CircleAlert size={14} strokeWidth={1.75} color="#FF5353" />
              <Text className="text-[12px] leading-[16px] text-[#FF5353]">{error}</Text>
            </View>
          ) : null}
        </View>
      </SettingsRow>

      <SettingsRow
        testID="settings-recovery-rotate"
        title="Rotate recovery key"
        description="Generate a brand-new recovery phrase and replace your existing backup. Use this if you think your current phrase may be compromised. Your old phrase stops working immediately."
      >
        <Pressable
          onPress={() => {
            void handleRotate();
          }}
          disabled={busy}
          accessibilityRole="button"
          className={`self-start flex flex-row items-center gap-2 px-3 py-2 rounded-[8px] border border-light-border-subtle dark:border-[#22242A] ${
            busy
              ? 'bg-light-surface-elevated dark:bg-[#15171C] opacity-60'
              : 'bg-light-surface-elevated dark:bg-[#15171C] active:opacity-70'
          }`}
        >
          <RefreshCw size={14} strokeWidth={1.75} color="#B5BAC3" />
          <Text className="text-[12px] leading-[16px] font-semibold text-light-text-primary dark:text-[#F5F6F8]">
            {busy ? 'Working…' : 'Rotate recovery key'}
          </Text>
        </Pressable>
      </SettingsRow>

      <SettingsRow
        testID="settings-recovery-how-it-works"
        title="How recovery works"
        description="Your account identity is encrypted locally with your recovery phrase using the same backup envelope as the mobile apps. Only the encrypted blob leaves your device. The phrase never reaches Ping's servers and cannot be reset by support."
      />
    </View>
  );
}

// ── Legacy passphrase pane (PBKDF2 upload) ─────────────────────────

interface LegacyRecoveryPaneProps {
  readonly onUpload: (input: RecoveryUploadInput) => Promise<RecoveryUploadResult>;
  readonly onCopyToClipboard: (text: string) => Promise<void> | void;
  readonly onDownload: (text: string, filename: string) => Promise<void> | void;
  readonly status?: RecoveryBackupStatus;
}

function LegacyRecoveryPane(props: LegacyRecoveryPaneProps): JSX.Element {
  const { onUpload, onCopyToClipboard, onDownload, status } = props;

  // Held as state so "Generate new" can rotate it without remount.
  // Lazy initialisation keeps the same value across re-renders when
  // the user clicks Copy / Download.
  const [passphrase, setPassphrase] = useState<string>(() => generateRecoveryPassphrase());
  const [acknowledged, setAcknowledged] = useState(false);
  const [copied, setCopied] = useState(false);
  const [downloaded, setDownloaded] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<RecoveryUploadResult | null>(null);

  const handleRotate = useCallback(() => {
    setPassphrase(generateRecoveryPassphrase());
    setAcknowledged(false);
    setCopied(false);
    setDownloaded(false);
    setResult(null);
    setError(null);
  }, []);

  const handleCopy = useCallback(async () => {
    try {
      await onCopyToClipboard(passphrase);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      setError(`Couldn't copy: ${(err as Error).message}`);
    }
  }, [onCopyToClipboard, passphrase]);

  const handleDownload = useCallback(async () => {
    try {
      const stamp = new Date().toISOString().slice(0, 10);
      await onDownload(formatDownload(passphrase), `ping-recovery-key-${stamp}.txt`);
      setDownloaded(true);
    } catch (err) {
      setError(`Couldn't download: ${(err as Error).message}`);
    }
  }, [onDownload, passphrase]);

  const handleUpload = useCallback(async () => {
    if (!acknowledged) {
      setError('Confirm you have saved the key before uploading.');
      return;
    }
    setPending(true);
    setError(null);
    try {
      const r = await onUpload({ passphrase });
      setResult(r);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setPending(false);
    }
  }, [acknowledged, passphrase, onUpload]);

  const statusLine = useMemo(() => describeStatus(status, result), [status, result]);

  return (
    <View className="flex flex-col gap-4">
      <SettingsRow
        variant="hero"
        testID="settings-recovery-intro"
        title="Account recovery"
        description="If you lose every device signed in to Ping, an encrypted backup is the only way to recover your account. Save the key below somewhere safe before uploading."
      />

      {statusLine !== null ? (
        <SettingsRow testID="settings-recovery-backup-status" title="Backup status">
          <StatusBadge tone={statusLine.tone} text={statusLine.text} />
        </SettingsRow>
      ) : null}

      <SettingsRow
        testID="settings-recovery-phrase"
        title="Recovery key"
        description="A 120-bit random key generated on this device. Ping cannot read it — if you lose it, the backup is irrecoverable."
      >
        <View className="flex flex-col gap-3 max-w-[420px]">
          <KeyTile value={passphrase} />

          <View className="flex flex-row gap-2">
            <SecondaryAction
              label={copied ? 'Copied' : 'Copy'}
              icon={copied ? Check : Copy}
              tone={copied ? 'success' : 'neutral'}
              onPress={() => {
                void handleCopy();
              }}
            />
            <SecondaryAction
              label={downloaded ? 'Saved' : 'Download'}
              icon={downloaded ? Check : Download}
              tone={downloaded ? 'success' : 'neutral'}
              onPress={() => {
                void handleDownload();
              }}
            />
            <SecondaryAction
              label="New key"
              icon={RefreshCw}
              tone="neutral"
              onPress={handleRotate}
            />
          </View>

          <AcknowledgeRow
            checked={acknowledged}
            onToggle={() => setAcknowledged((v) => !v)}
          />

          <Pressable
            onPress={() => {
              void handleUpload();
            }}
            disabled={pending || !acknowledged}
            className={`
              self-start flex flex-row items-center gap-2
              px-4 py-2 rounded-pill
              ${
                pending || !acknowledged
                  ? 'bg-light-surface-elevated dark:bg-dark-surface-elevated opacity-60'
                  : 'bg-light-accent-brand dark:bg-dark-accent-brand active:opacity-80'
              }
            `}
          >
            <ShieldCheck
              size={16}
              strokeWidth={1.75}
              color={pending || !acknowledged ? '#B5BAC3' : '#FFFFFF'}
            />
            <Text
              className={`text-[13px] leading-[18px] font-semibold ${
                pending || !acknowledged
                  ? 'text-light-text-secondary dark:text-dark-text-secondary'
                  : 'text-light-text-onAccent dark:text-dark-text-onAccent'
              }`}
            >
              {pending ? 'Uploading backup…' : 'Encrypt & upload backup'}
            </Text>
          </Pressable>

          {error !== null ? (
            <View className="flex flex-row items-center gap-2">
              <CircleAlert size={14} strokeWidth={1.75} color="#FF5353" />
              <Text className="text-[12px] leading-[16px] text-[#FF5353]">
                {error}
              </Text>
            </View>
          ) : null}

          {result !== null ? (
            <View className="flex flex-row items-center gap-2">
              <Check size={14} strokeWidth={2} color="#3CCB4F" />
              <Text className="text-[12px] leading-[16px] text-[#3CCB4F]">
                Backup uploaded ({formatBytes(result.bytes)} encrypted).
              </Text>
            </View>
          ) : null}
        </View>
      </SettingsRow>

      <SettingsRow
        testID="settings-recovery-how-it-works"
        title="How recovery works"
        description="The key encrypts your account identity locally using PBKDF2-SHA256 (600 000 iterations) and AES-256-GCM. Only the encrypted blob leaves your device. The key never reaches Ping's servers and cannot be reset by support."
      />
    </View>
  );
}

// ── Key tile (groups of 4, mono font) ──────────────────────────────

function KeyTile({ value }: { readonly value: string }): JSX.Element {
  const groups = value.split('-');
  return (
    <View className="w-full rounded-[10px] border border-light-border-subtle dark:border-[#22242A] bg-light-surface-primary dark:bg-[#11131A] px-4 py-3">
      <View className="flex flex-row flex-wrap gap-3">
        {groups.map((g, i) => (
          <Text
            // biome-ignore lint/suspicious/noArrayIndexKey: groups are a fixed-length immutable list
            key={i}
            selectable
            className="font-mono font-bold text-[15px] leading-[20px] tracking-[1.5px] text-light-text-primary dark:text-[#F5F6F8]"
          >
            {g}
          </Text>
        ))}
      </View>
    </View>
  );
}

// ── Status badge ───────────────────────────────────────────────────

interface StatusBadgeProps {
  readonly tone: 'success' | 'warn' | 'neutral';
  readonly text: string;
}

function StatusBadge({ tone, text }: StatusBadgeProps): JSX.Element {
  const containerClass =
    tone === 'success'
      ? 'bg-[#3CCB4F]/[0.12]'
      : tone === 'warn'
        ? 'bg-[#FFB323]/[0.12]'
        : 'bg-light-surface-elevated dark:bg-[#15171C]';
  const textClass =
    tone === 'success'
      ? 'text-[#3CCB4F]'
      : tone === 'warn'
        ? 'text-[#FFB323]'
        : 'text-light-text-secondary dark:text-[#B5BAC3]';
  const iconColor =
    tone === 'success' ? '#3CCB4F' : tone === 'warn' ? '#FFB323' : '#B5BAC3';
  const Icon = tone === 'success' ? Check : tone === 'warn' ? CircleAlert : ShieldCheck;
  return (
    <View
      className={`self-start inline-flex flex-row items-center gap-2 px-3 py-1.5 rounded-full ${containerClass}`}
    >
      <Icon size={14} strokeWidth={1.75} color={iconColor} />
      <Text className={`text-[13px] leading-[18px] font-semibold ${textClass}`}>
        {text}
      </Text>
    </View>
  );
}

// ── Secondary action (Copy / Download / New key) ───────────────────

interface SecondaryActionProps {
  readonly label: string;
  readonly icon: typeof Copy;
  readonly tone: 'neutral' | 'success';
  readonly onPress: () => void;
}

function SecondaryAction({
  label,
  icon: Icon,
  tone,
  onPress,
}: SecondaryActionProps): JSX.Element {
  const bgClass =
    tone === 'success'
      ? 'bg-[#3CCB4F]/[0.12]'
      : 'bg-light-surface-elevated dark:bg-[#15171C]';
  const textClass =
    tone === 'success'
      ? 'text-[#3CCB4F]'
      : 'text-light-text-primary dark:text-[#F5F6F8]';
  const iconColor = tone === 'success' ? '#3CCB4F' : '#B5BAC3';
  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="button"
      className={`
        flex flex-row items-center gap-2
        px-3 py-2 rounded-[8px] ${bgClass}
        border border-light-border-subtle dark:border-[#22242A]
        active:opacity-70
      `}
    >
      <Icon size={14} strokeWidth={1.75} color={iconColor} />
      <Text className={`text-[12px] leading-[16px] font-semibold ${textClass}`}>
        {label}
      </Text>
    </Pressable>
  );
}

// ── Acknowledgement checkbox ───────────────────────────────────────

function AcknowledgeRow({
  checked,
  onToggle,
}: {
  readonly checked: boolean;
  readonly onToggle: () => void;
}): JSX.Element {
  return (
    <Pressable
      onPress={onToggle}
      accessibilityRole="checkbox"
      accessibilityState={{ checked }}
      className="flex flex-row items-start gap-2 py-1"
    >
      <View
        className={`w-[18px] h-[18px] rounded-[5px] border items-center justify-center mt-[2px] ${
          checked
            ? 'bg-light-accent-brand dark:bg-dark-accent-brand border-light-accent-brand dark:border-dark-accent-brand'
            : 'bg-transparent border-light-border-strong dark:border-[#3F434B]'
        }`}
      >
        {checked ? <Check size={12} strokeWidth={3} color="#FFFFFF" /> : null}
      </View>
      <Text className="flex-1 text-[12px] leading-[16px] text-light-text-primary dark:text-[#F5F6F8]">
        I've saved this key somewhere safe.
      </Text>
    </Pressable>
  );
}

// ── Helpers ────────────────────────────────────────────────────────

function describeStatus(
  status: RecoveryBackupStatus | undefined,
  freshlyUploaded: RecoveryUploadResult | null,
): { readonly tone: 'success' | 'warn'; readonly text: string } | null {
  if (freshlyUploaded !== null) {
    return { tone: 'success', text: 'Backup is up to date' };
  }
  if (status === undefined) return null;
  switch (status.kind) {
    case 'completed':
      return {
        tone: 'success',
        text: `Backup uploaded ${formatRelative(status.uploadedAt)}`,
      };
    case 'skipped':
      return {
        tone: 'warn',
        text: `No backup yet — set one up to protect your account`,
      };
    case 'never':
      return {
        tone: 'warn',
        text: 'No backup yet — set one up to protect your account',
      };
  }
}

function formatRelative(timestamp: number): string {
  const diffMs = Date.now() - timestamp;
  if (diffMs < 0) return 'just now';
  const minutes = Math.floor(diffMs / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(timestamp).toLocaleDateString();
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDownload(passphrase: string): string {
  const stamp = new Date().toISOString();
  return [
    'Ping account recovery key',
    '',
    `Generated:    ${stamp}`,
    `Recovery key: ${passphrase}`,
    '',
    'Keep this somewhere safe. Without it, if you lose every device',
    'signed in to Ping, you will permanently lose access to your account.',
    'Ping support cannot recover or reset this key for you.',
    '',
  ].join('\n');
}
